/**
 * ProctoringTest - Live testing page for the proctoring system
 *
 * This page lets you:
 * - See your webcam feed live
 * - Watch verification results in real-time
 * - Adjust all proctoring thresholds with sliders
 * - See an event log of all verification attempts
 * - Test edge cases (no face, multiple faces, wrong person)
 */

import { useState, useEffect, useRef, useCallback } from 'react'
import { useProctoringConfigStore } from '../../store/proctoringConfigStore'
import { enrollFaceFromBase64, verifyFaceFromBase64 } from '../../services/faceDetection'
import Webcam from '../Webcam'
import { useWebcamCapture } from '../Webcam'
import ConfigPanel from './ConfigPanel'
import StatusDisplay from './StatusDisplay'
import EventLog, { ProctoringEvent } from './EventLog'

// ─────────────────────────────────────────────────────────────────────────────
// Types
// ─────────────────────────────────────────────────────────────────────────────

interface VerificationResult {
  timestamp: number
  facesDetected: number
  similarity: number
  verified: boolean
  error?: string
}

type TestState = 'idle' | 'enrolling' | 'enrolled' | 'verifying' | 'suspended'

// ─────────────────────────────────────────────────────────────────────────────
// Component
// ─────────────────────────────────────────────────────────────────────────────

export default function ProctoringTest() {
  // Set page title
  useEffect(() => {
    document.title = 'ReallyAuthenticate - Proctoring System'
  }, [])

  // Config store
  const config = useProctoringConfigStore()

  // Test state
  const [testState, setTestState] = useState<TestState>('idle')
  const [sessionToken, setSessionToken] = useState<string | null>(null)
  const [encodingId, setEncodingId] = useState<string | null>(null)
  const [enrolledFaceImage, setEnrolledFaceImage] = useState<string | null>(null)

  // Live verification results
  const [lastResult, setLastResult] = useState<VerificationResult | null>(null)
  const [consecutiveFailures, setConsecutiveFailures] = useState(0)
  const [currentScore, setCurrentScore] = useState(100)

  // Violation counts for score calculation display
  const [violationCounts, setViolationCounts] = useState({
    noFace: 0,
    multipleFaces: 0,
    identityMismatch: 0,
    tabSwitch: 0,
  })

  // Event log
  const [events, setEvents] = useState<ProctoringEvent[]>([])

  // Refs
  const verificationIntervalRef = useRef<NodeJS.Timeout | null>(null)
  const { capture } = useWebcamCapture()

  // ─────────────────────────────────────────────────────────────────
  // Event Logging
  // ─────────────────────────────────────────────────────────────────

  const addEvent = useCallback((
    type: ProctoringEvent['type'],
    message: string,
    details?: string
  ) => {
    const event: ProctoringEvent = {
      id: Date.now().toString(),
      timestamp: Date.now(),
      type,
      message,
      details,
    }
    setEvents((prev) => [event, ...prev].slice(0, 100)) // Keep last 100 events
  }, [])

  // ─────────────────────────────────────────────────────────────────
  // Score Calculation (mirrors assessmentStore logic)
  // ─────────────────────────────────────────────────────────────────

  const calculateScore = useCallback((
    identityMatch: number,
    faceDetected: boolean,
    violationCount: number
  ) => {
    const { scoringWeights, violationPenalties } = config

    const identityScore = identityMatch
    const presenceScore = faceDetected ? 100 : 0
    const behaviorScore = Math.max(0, 100 - violationCount * violationPenalties.identityMismatch)

    return Math.round(
      identityScore * scoringWeights.identity +
      presenceScore * scoringWeights.presence +
      behaviorScore * scoringWeights.behavior
    )
  }, [config])

  // ─────────────────────────────────────────────────────────────────
  // Create Test Session
  // ─────────────────────────────────────────────────────────────────

  const createTestSession = async (): Promise<string | null> => {
    try {
      const response = await fetch(
        'https://reallyassess-face-api-dev.techtree.workers.dev/api/sessions',
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ learnerName: 'Proctoring Test User', metadata: { test: true } }),
        }
      )

      if (!response.ok) {
        throw new Error('Failed to create session')
      }

      const data = await response.json() as { token?: string; sessionToken?: string }
      return data.token || data.sessionToken || null
    } catch (error) {
      addEvent('error', 'Failed to create session', String(error))
      return null
    }
  }

  // ─────────────────────────────────────────────────────────────────
  // Enrollment
  // ─────────────────────────────────────────────────────────────────

  const handleEnroll = async () => {
    setTestState('enrolling')
    addEvent('info', 'Starting face enrollment...')

    // Create session if needed
    let token = sessionToken
    if (!token) {
      token = await createTestSession()
      if (!token) {
        setTestState('idle')
        return
      }
      setSessionToken(token)
      addEvent('success', 'Session created')
    }

    // Capture face
    const imageBase64 = capture()
    if (!imageBase64) {
      addEvent('error', 'Failed to capture webcam frame')
      setTestState('idle')
      return
    }

    // Enroll
    const result = await enrollFaceFromBase64(imageBase64, token)

    if (result.success && result.encodingId) {
      setEncodingId(result.encodingId)
      setEnrolledFaceImage(imageBase64) // Save the enrolled face image
      setTestState('enrolled')
      setConsecutiveFailures(0)
      setCurrentScore(100)
      addEvent('success', 'Face enrolled successfully', `Encoding ID: ${result.encodingId.slice(0, 8)}...`)
    } else {
      addEvent('error', 'Enrollment failed', result.error)
      setTestState('idle')
    }
  }

  // ─────────────────────────────────────────────────────────────────
  // Single Verification Cycle
  // ─────────────────────────────────────────────────────────────────

  const runVerification = useCallback(async () => {
    if (!sessionToken || testState === 'suspended') return

    const imageBase64 = capture()
    if (!imageBase64) {
      addEvent('warning', 'No webcam frame available')
      setLastResult({
        timestamp: Date.now(),
        facesDetected: 0,
        similarity: 0,
        verified: false,
        error: 'no_capture',
      })
      return
    }

    try {
      const result = await verifyFaceFromBase64(imageBase64, encodingId || undefined, sessionToken)

      const verificationResult: VerificationResult = {
        timestamp: Date.now(),
        facesDetected: result.facesDetected,
        similarity: result.similarity || 0,
        verified: result.verified,
        error: result.error,
      }

      setLastResult(verificationResult)

      // Handle different cases
      if (result.facesDetected > 1) {
        // Multiple faces - instant suspend
        setViolationCounts(prev => ({ ...prev, multipleFaces: prev.multipleFaces + 1 }))
        addEvent('error', `Multiple faces detected (${result.facesDetected})`, 'SUSPENDED')
        setTestState('suspended')
        setConsecutiveFailures(config.maxConsecutiveIdentityFailures)
        return
      }

      if (result.facesDetected === 0 || result.error === 'no_face_detected') {
        setViolationCounts(prev => ({ ...prev, noFace: prev.noFace + 1 }))
        addEvent('warning', 'No face detected')
        return
      }

      // Single face - check identity
      const similarity = result.similarity || 0
      const isMatch = similarity >= config.identitySimilarityThreshold || result.verified

      if (isMatch) {
        // Success - reset failures
        setConsecutiveFailures(0)
        setCurrentScore(calculateScore(similarity, true, 0))
        addEvent('success', `Face verified (${Math.round(similarity)}% match)`)
      } else {
        // Failure - increment counter
        const newFailures = consecutiveFailures + 1
        setConsecutiveFailures(newFailures)
        setViolationCounts(prev => ({ ...prev, identityMismatch: prev.identityMismatch + 1 }))
        setCurrentScore(calculateScore(similarity, true, newFailures))

        if (newFailures >= config.maxConsecutiveIdentityFailures) {
          addEvent('error', `Identity mismatch (${Math.round(similarity)}%)`, `${newFailures}/${config.maxConsecutiveIdentityFailures} strikes - SUSPENDED`)
          setTestState('suspended')
        } else {
          addEvent('warning', `Identity mismatch (${Math.round(similarity)}%)`, `Strike ${newFailures}/${config.maxConsecutiveIdentityFailures}`)
        }
      }
    } catch (error) {
      addEvent('error', 'Verification error', String(error))
    }
  }, [sessionToken, encodingId, testState, capture, config, consecutiveFailures, calculateScore, addEvent])

  // ─────────────────────────────────────────────────────────────────
  // Start/Stop Verification Loop
  // ─────────────────────────────────────────────────────────────────

  const startVerification = useCallback(() => {
    if (testState !== 'enrolled') return

    setTestState('verifying')
    addEvent('info', `Starting verification loop (every ${config.verificationIntervalMs}ms)`)

    // Run immediately, then on interval
    runVerification()
    verificationIntervalRef.current = setInterval(runVerification, config.verificationIntervalMs)
  }, [testState, config.verificationIntervalMs, runVerification, addEvent])

  const stopVerification = useCallback(() => {
    if (verificationIntervalRef.current) {
      clearInterval(verificationIntervalRef.current)
      verificationIntervalRef.current = null
    }

    if (testState === 'verifying') {
      setTestState('enrolled')
      addEvent('info', 'Verification stopped')
    }
  }, [testState, addEvent])

  // ─────────────────────────────────────────────────────────────────
  // Reset
  // ─────────────────────────────────────────────────────────────────

  const handleReset = () => {
    stopVerification()
    setTestState('idle')
    setSessionToken(null)
    setEncodingId(null)
    setEnrolledFaceImage(null)
    setLastResult(null)
    setConsecutiveFailures(0)
    setCurrentScore(100)
    setViolationCounts({ noFace: 0, multipleFaces: 0, identityMismatch: 0, tabSwitch: 0 })
    setEvents([])
    addEvent('info', 'Test reset')
  }

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (verificationIntervalRef.current) {
        clearInterval(verificationIntervalRef.current)
      }
    }
  }, [])

  // Update interval when config changes (while verifying)
  useEffect(() => {
    if (testState === 'verifying' && verificationIntervalRef.current) {
      clearInterval(verificationIntervalRef.current)
      verificationIntervalRef.current = setInterval(runVerification, config.verificationIntervalMs)
    }
  }, [config.verificationIntervalMs, testState, runVerification])

  // ─────────────────────────────────────────────────────────────────
  // Render
  // ─────────────────────────────────────────────────────────────────

  return (
    <div className="min-h-screen bg-gray-900 text-white p-4">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-2xl font-bold">ReallyAuthenticate</h1>
            <p className="text-gray-400">Test and tune proctoring thresholds in real-time</p>
            <p className="text-gray-500 text-xs mt-1">
              Complete: POST /api/sessions/:sessionId/complete · Integrity: GET /api/sessions/:sessionId/integrity
            </p>
          </div>
          <div className="flex gap-2">
            <button
              onClick={handleReset}
              className="px-4 py-2 bg-gray-700 hover:bg-gray-600 rounded-lg transition-colors"
            >
              Reset
            </button>
            <a
              href="/"
              className="px-4 py-2 bg-gray-700 hover:bg-gray-600 rounded-lg transition-colors"
            >
              Back to App
            </a>
          </div>
        </div>

        {/* Main Layout */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Left Column - Webcam & Controls */}
          <div className="lg:col-span-1 space-y-4">
            {/* Webcam */}
            <div className="bg-gray-800 rounded-xl p-4">
              <h2 className="text-lg font-semibold mb-3">Live Webcam</h2>
              <div className="aspect-[4/3] bg-black rounded-lg overflow-hidden relative">
                <Webcam showCapture={false} className="w-full h-full object-cover" />

                {/* Status overlay */}
                <div className="absolute bottom-2 left-2 right-2">
                  <div className={`px-3 py-1 rounded-full text-sm font-medium inline-flex items-center gap-2 ${
                    testState === 'suspended' ? 'bg-red-500/90' :
                    testState === 'verifying' ? 'bg-green-500/90' :
                    testState === 'enrolled' ? 'bg-blue-500/90' :
                    testState === 'enrolling' ? 'bg-yellow-500/90' :
                    'bg-gray-700/90'
                  }`}>
                    <span className={`w-2 h-2 rounded-full ${
                      testState === 'verifying' ? 'bg-white animate-pulse' : 'bg-white/50'
                    }`} />
                    {testState === 'idle' && 'Ready to enroll'}
                    {testState === 'enrolling' && 'Enrolling...'}
                    {testState === 'enrolled' && 'Enrolled - Ready to verify'}
                    {testState === 'verifying' && 'Verifying...'}
                    {testState === 'suspended' && 'SUSPENDED'}
                  </div>
                </div>
              </div>

              {/* Control Buttons */}
              <div className="mt-4 flex flex-wrap gap-2">
                {testState === 'idle' && (
                  <button
                    onClick={handleEnroll}
                    className="flex-1 px-4 py-2 bg-blue-600 hover:bg-blue-500 rounded-lg font-medium transition-colors"
                  >
                    Enroll Face
                  </button>
                )}

                {testState === 'enrolled' && (
                  <button
                    onClick={startVerification}
                    className="flex-1 px-4 py-2 bg-green-600 hover:bg-green-500 rounded-lg font-medium transition-colors"
                  >
                    Start Verification
                  </button>
                )}

                {testState === 'verifying' && (
                  <button
                    onClick={stopVerification}
                    className="flex-1 px-4 py-2 bg-yellow-600 hover:bg-yellow-500 rounded-lg font-medium transition-colors"
                  >
                    Stop Verification
                  </button>
                )}

                {testState === 'suspended' && (
                  <button
                    onClick={handleReset}
                    className="flex-1 px-4 py-2 bg-red-600 hover:bg-red-500 rounded-lg font-medium transition-colors"
                  >
                    Reset (Suspended)
                  </button>
                )}
              </div>
            </div>

            {/* Enrolled Face Reference - same size as webcam */}
            {enrolledFaceImage && (
              <div className="bg-gray-800 rounded-xl p-4">
                <h2 className="text-lg font-semibold mb-3">Reference Face (Verifying Against)</h2>
                <div className="aspect-[4/3] bg-gray-900 rounded-lg overflow-hidden border-2 border-blue-500 relative">
                  <img
                    src={`data:image/jpeg;base64,${enrolledFaceImage}`}
                    alt="Enrolled face"
                    className="w-full h-full object-cover"
                  />
                  <div className="absolute bottom-2 left-2">
                    <div className="bg-blue-500/90 text-white text-sm px-3 py-1 rounded-full">
                      Enrolled Reference
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Live Status */}
            <StatusDisplay
              lastResult={lastResult}
              consecutiveFailures={consecutiveFailures}
              maxFailures={config.maxConsecutiveIdentityFailures}
              similarityThreshold={config.identitySimilarityThreshold}
              currentScore={currentScore}
              passingScore={config.passingScore}
              isVerifying={testState === 'verifying'}
              isSuspended={testState === 'suspended'}
              violationCounts={violationCounts}
            />
          </div>

          {/* Middle Column - Config Panel */}
          <div className="lg:col-span-1">
            <ConfigPanel />
          </div>

          {/* Right Column - Event Log */}
          <div className="lg:col-span-1">
            <EventLog events={events} onClear={() => setEvents([])} />
          </div>
        </div>
      </div>
    </div>
  )
}
